import axios, { AxiosError, AxiosResponse } from 'axios';
import jsCookie from 'js-cookie';
import Router from 'next/router';
import { LogError } from 'utils/errorlogger';

import { demoModeExcludedAPIs } from '@/shared/constant/general';
import { clearStoreData } from '@/shared/hooks/context/createGenericContext';
import { IstoreData } from '@/shared/hooks/context/createGenericContext/context';
import { errorRespons } from '@/shared/lib/axios/axios';
interface ServerError {
  status: string;
  message?: string;
  errorCode?: string;
}

const axiosClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BASE_API_URL,
});

axiosClient.interceptors.request.use(
  (config) => {
    // check for user in cookies first
    const token = jsCookie.get('token');
    // const lang = jsCookie.get('lang') || 'en';

    if (token !== null && token !== '') {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (Router.locale) {
      config.headers['Accept-Language'] = Router.locale;
    }
    // check if the user is on demo mode
    if (
      typeof config.url === 'string' &&
      !demoModeExcludedAPIs.exact.includes(config?.url) &&
      !demoModeExcludedAPIs.partial.some((url) =>
        config.url?.startsWith(url)
      ) &&
      !demoModeExcludedAPIs.pattern.some((pattern) =>
        pattern.test(config.url as string)
      )
    ) {
      const storeData = localStorage.getItem('storeData');
      if (storeData) {
        const parsedStore: IstoreData = JSON.parse(storeData);
        if (parsedStore?.isDemoFarm) config.baseURL = '/api/demo';
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosClient.interceptors.response.use(
  (config: AxiosResponse) => {
    if (config.data.message === 'new token created') {
      const token = jsCookie.get('token');

      if (token !== null && token !== '') {
        jsCookie.set('token', config.data.token);
      }
      // message.info('Your token has been refreshed, please request again!');
    }
    return config.data;
  },
  (error: AxiosError<ServerError>) => {
    const statusCode = error.response?.status;

    if (statusCode === 401) {
      // if u want to redirect to login
      jsCookie.remove('token', { path: '/' });
      jsCookie.remove('userObj', { path: '/' });
      clearStoreData();
      // TODO :: need to use useRouter hook instead of window.location to save SPA
      window.location.href = '/login';
    }
    if (statusCode === 404) {
      //
    }

    const errorMessage = error.response?.data.message || error.message;
    let newErrorMessage = '';

    if (containsSQLContent(errorMessage)) {
      newErrorMessage = 'Database Error';
    } else {
      newErrorMessage = errorMessage;
    }

    const obj: errorRespons = {
      msg: newErrorMessage,
      status: error.response?.status,
      errorResponseData: error?.response?.data,
      errorCode: error?.response?.data?.errorCode
        ? error?.response?.data?.errorCode
        : undefined,
    };

    LogError(error);
    return Promise.reject(obj);
  }
);

export default axiosClient;

// TODO: temp solution for sql errors
export const containsSQLContent = (input: string) => {
  const sqlIndicators = [
    'sql', // Generic SQL references
    'table', // References to tables
    'column', // References to columns
    'database', // General database references
    '\\bSELECT\\b', // Common SQL keywords
    '\\bINSERT\\b',
    '\\bUPDATE\\b',
    '\\bDELETE\\b',
    '\\bDROP\\b',
    '\\bCREATE\\b',
    '\\bALTER\\b',
    '\\bWHERE\\b',
    '\\bFROM\\b',
    '\\bJOIN\\b',
    '\\bUNION\\b',
    '\\bGROUP BY\\b',
    '\\bORDER BY\\b',
    '\\bHAVING\\b',
    '\\bLIMIT\\b',
    '\\bVALUES\\b', // Insert or update values
    '\\bSET\\b', // Update queries
    '[a-zA-Z_]+\\.[a-zA-Z_]+', // Table.column patterns
  ];

  const pattern = new RegExp(sqlIndicators.join('|'), 'i'); // Case-insensitive
  return pattern.test(input);
};

export const getErrorMessage = (
  e: errorRespons,
  defaultMessage = 'try again!'
) => {
  return typeof e?.msg === 'string' ? e?.msg : defaultMessage;
};
export const getErrorCode = (e: errorRespons, defaultMessage = undefined) => {
  return typeof e?.errorCode === 'string' ? e?.errorCode : defaultMessage;
};
